import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiUser, FiLogOut } from 'react-icons/fi';

const Navbar = ({ isAuthenticated, userRole, logout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const navbarClasses = `fixed inset-x-0 top-0 z-50 border-b border-slate-800 bg-slate-950 transition-all duration-200 ${
    scrolled ? 'py-3' : 'py-4'
  }`;

  const dashboardLink = () => {
    switch (userRole) {
      case 'admin':
        return '/admin/dashboard';
      case 'staff':
        return '/staff/dashboard';
      case 'mechanic':
        return '/mechanic/dashboard';
      case 'owner':
        return '/owner/dashboard';
      default:
        return '/customer/dashboard';
    }
  };

  const renderAuthLinks = () => {
    if (!isAuthenticated) {
      return (
        <>
          <Link 
            to="/login" 
            className="flex items-center whitespace-nowrap text-sm font-medium text-white/90 transition-colors hover:text-primary"
          >
            <FiUser className="mr-1" />
            Login
          </Link>
          <Link 
            to="/register" 
            className="ml-4 inline-flex items-center whitespace-nowrap rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-dark"
          >
            Register
          </Link>
        </>
      );
    }

    return (
      <>
        <Link 
          to={dashboardLink()} 
          className="flex items-center whitespace-nowrap text-sm font-medium text-white/90 transition-colors hover:text-primary"
        >
          <FiUser className="mr-1" /> Dashboard
        </Link>
        <button 
          onClick={logout} 
          className="ml-4 flex items-center whitespace-nowrap text-sm font-medium text-white/90 transition-colors hover:text-primary"
        >
          <FiLogOut className="mr-1" /> Logout
        </button>
      </>
    );
  };

  const getNavLinkClass = (path) => {
    const baseClass = 'group relative flex items-center text-sm font-medium text-white/85 transition-colors hover:text-primary';
    const activeClass = location.pathname === path ? 'text-primary' : '';

    return `${baseClass} ${activeClass} whitespace-nowrap group`;
  };

  const renderActiveDot = (path) => {
    if (location.pathname === path) {
      return <span className="absolute -bottom-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-primary"></span>;
    }
    return null;
  };

  return (
    <nav className={navbarClasses}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center whitespace-nowrap">
            <div className="mr-3 flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 text-lg font-bold text-white">
              <span className="text-primary">X</span>S
            </div>
            <div>
              <div className="display-font text-xl font-bold text-white">XSignature</div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-slate-500">Lighting & Parts</div>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center">
            <div className="mr-8 flex flex-wrap items-center">
              <Link to="/" className={`${getNavLinkClass('/')} mr-8`}>
                Home
                {renderActiveDot('/')}
                <span className="absolute bottom-[-10px] left-0 right-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform group-hover:scale-x-100"></span>
              </Link>
              <Link to="/services" className={`${getNavLinkClass('/services')} mr-8`}>
                Services
                {renderActiveDot('/services')}
                <span className="absolute bottom-[-10px] left-0 right-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform group-hover:scale-x-100"></span>
              </Link>
              <Link to="/about" className={`${getNavLinkClass('/about')} mr-8`}>
                About
                {renderActiveDot('/about')}
                <span className="absolute bottom-[-10px] left-0 right-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform group-hover:scale-x-100"></span>
              </Link>
              <Link to="/contact" className={`${getNavLinkClass('/contact')}`}>
                Contact
                {renderActiveDot('/contact')}
                <span className="absolute bottom-[-10px] left-0 right-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform group-hover:scale-x-100"></span>
              </Link>
            </div>
            <div className="flex items-center">
              {renderAuthLinks()}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2 rounded-full hover:bg-white/10 focus:outline-none transition-colors"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="mt-4 max-h-[80vh] overflow-y-auto rounded-3xl border border-slate-800 bg-slate-950 p-6 animate-fadeDown md:hidden">
            <div className="flex flex-col space-y-5">
              <Link 
                to="/" 
                className={`border-b border-white/10 py-2 text-white transition-colors hover:text-primary ${
                  location.pathname === '/' ? 'text-primary font-medium' : ''
                }`}
                onClick={toggleMenu}
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className={`border-b border-white/10 py-2 text-white transition-colors hover:text-primary ${
                  location.pathname === '/services' ? 'text-primary font-medium' : ''
                }`}
                onClick={toggleMenu}
              >
                Services
              </Link>
              <Link 
                to="/about" 
                className={`border-b border-white/10 py-2 text-white transition-colors hover:text-primary ${
                  location.pathname === '/about' ? 'text-primary font-medium' : ''
                }`}
                onClick={toggleMenu}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className={`py-2 text-white transition-colors hover:text-primary ${
                  location.pathname === '/contact' ? 'text-primary font-medium' : ''
                }`}
                onClick={toggleMenu}
              >
                Contact
              </Link>
              <div className="pt-4 flex flex-col space-y-4">
                {!isAuthenticated ? (
                  <>
                    <Link 
                      to="/login" 
                      className="flex items-center justify-center whitespace-nowrap rounded-2xl border border-white/20 py-3 text-white transition-colors hover:text-primary"
                      onClick={toggleMenu}
                    >
                      <FiUser className="mr-2" /> Login
                    </Link>
                    <Link 
                      to="/register" 
                      className="flex items-center justify-center whitespace-nowrap rounded-2xl bg-primary py-3 text-white transition-colors hover:bg-primary-dark"
                      onClick={toggleMenu}
                    >
                      Register
                    </Link>
                  </>
                ) : (
                  <>
                    <Link 
                      to={dashboardLink()} 
                      className="flex items-center justify-center rounded-2xl border border-white/20 py-3 text-white transition-colors hover:text-primary"
                      onClick={toggleMenu}
                    >
                      <FiUser className="mr-2" /> Dashboard
                    </Link>
                    <button 
                      onClick={() => {logout(); toggleMenu();}} 
                      className="flex items-center justify-center rounded-2xl bg-primary py-3 text-white transition-colors hover:bg-primary-dark"
                    >
                      <FiLogOut className="mr-2" /> Logout
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 
